import axios from 'axios';
import { useEffect, useState, useRef, useCallback} from 'react';
import { RoomContext, useTranscriptions, useChat, useTracks, RoomAudioRenderer, BarVisualizer, TrackRefContext, VideoTrack, useTrackToggle, useRoomContext, useLocalParticipant, useVoiceAssistant } from '@livekit/components-react';
import { Room, RoomEvent,Track } from 'livekit-client';
import '@livekit/components-styles';
import { Button, Container, Card, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import "bootstrap-icons/font/bootstrap-icons.css";
import '../style.css';

const DEFAULT_AGENT_NAME = 'wowguide-agent';

function AvatarRenderer({ avatarImgSrc, startChat }) {
    const trackRefs = useTracks([Track.Source.Camera]);
    const camTrackRef = trackRefs.find(
        (trackRef) => trackRef.participant?.name?.includes(DEFAULT_AGENT_NAME)
    );

    useEffect(() => {
        if (!camTrackRef) {
            const intervalId = setInterval(() => {
                startChat(); 
            }, 15000);
            return () => clearInterval(intervalId);
        }
    }, [camTrackRef]);

    // Normal avatar video
    if (camTrackRef) {
        return (
            <div className="video-wrapper position-relative w-100 h-100">
                <VideoTrack
                    trackRef={camTrackRef}
                    className='video-el w-100 h-100'
                    style={{ objectFit: 'cover' }}
                />
                <RoomAudioRenderer />
            </div>
        );
    }

    // Loading placeholder until avatar loads 
    return (
        <div className="image-wrapper loading">
            <img className="avatar-img"
                src={avatarImgSrc}
                alt="preview"
            />
            <div className="image-overlay">
                <img src="/loader2.gif" alt="loading" className="loader-image" />
            </div>
        </div>
    );
}

const MuteButton = () => {

    const { buttonProps, enabled } = useTrackToggle({
        source: Track.Source.Microphone,
        initialState: true
    });

    return (
        <Button variant='light' onClick={buttonProps.onClick} className="control-btn rounded-circle mute-btn">
            <i className={`bi ${enabled ? "bi-mic-fill" : "bi-mic-mute-fill"}`} />
        </Button>
    );
};

const AudioWavesRenderer = ({ barCount = 8, ...props }) =>{
    const audioTracks = useTracks([Track.Source.Microphone]);
    const audioTrackRef = audioTracks[0];
          return (
        <TrackRefContext.Provider value={audioTrackRef}>
            <BarVisualizer barCount={barCount} options={{ minHeight: 8 }} {...props} />
        </TrackRefContext.Provider>
          );
      
}

const TranscriptBox = ({ showTranscript }) =>{
    const transcriptions = useTranscriptions();
    const { state } = useVoiceAssistant();
    const boxRef = useRef(null);

    const lastIsUser = transcriptions.length > 0 && !transcriptions[transcriptions.length - 1].participantInfo?.identity?.includes("agent");
    const isThinking = state === 'thinking' || (lastIsUser && state !== 'speaking');

    useEffect(() => {
        if (boxRef.current && showTranscript ) {
            boxRef.current.scrollTop = boxRef.current.scrollHeight;
        }
    }, [transcriptions, showTranscript, isThinking]);

    return (
        <div className="d-flex flex-column gap-2 h-100 overflow-auto p-3" ref={boxRef}>
            {transcriptions.map((transcription, idx) =>{
                const isAgent = transcription.participantInfo.identity.includes("agent");
                return (
                    <div key={idx} className={`d-flex ${isAgent ? "justify-content-start text-dark" : "justify-content-end text-light"}`}>
                        <div className={`p-2 rounded fw-bold chat-msg-bubble ${isAgent ? "chat-msg-agent" : "chat-msg-user"}`}>
                            <strong>{isAgent ? "Agent" : "You"}:</strong> {transcription.text}
                        </div>
                    </div>
                );
            })}
            {isThinking && (
                <div className="d-flex justify-content-start text-dark">
                    <div className="p-2 rounded fw-bold d-inline-flex align-items-center gap-2 bg-white chat-thinking-indicator">
                        <span className="spinner-grow spinner-grow-sm text-secondary chat-thinking-spinner" role="status"></span>
                        <span className="text-muted fst-italic">Thinking...</span>
                    </div>
                </div>
            )}
        </div>
    );
}

const MobileResultsList = ({ matches, activeAvatar }) => {
    const handleViewImage = async (url) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } catch (err) {
            console.error('Error opening image:', err);
            window.open(url, '_blank'); // Fallback
        }
    };

    return (
        <div className="h-100 thin-scrollbar border-light p-1" style={{ overflowY: 'auto' }}>
            {matches.map((match, idx) => {
                const imgPath = match.payload?.image_path;
                const imgName = match.payload?.image_name;
                const imgSrc = ((imgPath?.startsWith('http') || imgPath?.startsWith('https')) && !imgPath.includes('Images'))
                    ? imgPath : `/api/Images/${imgName}`;
                const rawTags = match.payload?.tags || [];
                const tags = Array.from(new Set(rawTags.flatMap(t => String(t).split(',').map(s => s.trim())).filter(Boolean)));

                return (
                    <Card key={idx} className="border-0 shadow-sm overflow-hidden bg-white text-dark rounded-4 mb-3" style={{ flexShrink: 0 }}>
                        <div className="position-relative" style={{ height: '180px', cursor: 'pointer' }} onClick={() => handleViewImage(imgSrc)}>
                            <Card.Img 
                                variant="top" 
                                src={imgSrc} 
                                style={{ height: '100%', objectFit: 'cover', objectPosition: 'center' }} 
                            />
                        </div>
                        <Card.Body className="p-3 text-start">
                            <h6 className="fw-bold mb-2 text-dark text-truncate" style={{ fontSize: '13px' }} title={match.payload?.image_name || "Artwork"}>
                                {match.payload?.image_name || "Untitled"}
                            </h6>
                            
                            {tags.length > 0 && (
                                <div className="mobile-tags-grid gap-1 my-3 w-100 thin-scrollbar pb-2">
                                    {tags?.map((tag, i) => (
                                        <span 
                                            key={i} 
                                            className="badge text-dark bg-light border fw-normal rounded-pill text-nowrap py-1 px-2" 
                                            style={{ fontSize: '10px', letterSpacing: '0.1px' }}
                                        >
                                            {tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase()}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="d-flex align-items-center justify-content-between">
                                {activeAvatar && (
                                    <Button
                                        variant="outline-dark"
                                        size="sm"
                                        className="py-1 px-3 rounded-pill fw-bold"
                                        style={{ fontSize: '11px' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.dispatchEvent(new CustomEvent('tell_info_agent', { detail: { imageName: match.payload?.image_name } }));
                                        }}
                                    >
                                        <i className="bi bi-info-circle-fill me-1"></i> Tell More
                                    </Button>
                                )}
                                <span className="badge bg-secondary rounded-pill" style={{ fontSize: '10px', padding: '4px 8px' }}>
                                    {match.payload?.model ? match.payload.model.charAt(0).toUpperCase() + match.payload.model.slice(1) : "Qwen"}
                                </span>
                            </div>
                        </Card.Body>
                    </Card>
                );
            })}
        </div>
    );
};




const ChatBox = ({ loading, setLoading }) =>{
    const room = useRoomContext();
    const transcriptions = useTranscriptions();
    const { state } = useVoiceAssistant();
    const boxRef = useRef(null);
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState([]);
    
    const { buttonProps, enabled: micEnabled } = useTrackToggle({
        source: Track.Source.Microphone,
        initialState: false
    });
    
    // Sync spoken transcriptions dynamically, updating existing text and adding new segments
    useEffect(() => {
        if (transcriptions.length > 0) {
            setLoading('')
            setMessages(prev => {
                if (prev.length === 0) {
                    return transcriptions.map((t, idx) => ({
                        text: t.text,
                        isAgent: t.participantInfo?.identity?.includes("agent"),
                        segmentIndex: idx
                    }));
                }
                const lastTranscript = transcriptions[transcriptions.length - 1];
                const isAgent = lastTranscript.participantInfo?.identity?.includes("agent");
                const segmentIndex = transcriptions.length - 1;
                
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.segmentIndex === segmentIndex) {
                    const updated = [...prev];
                    updated[prev.length - 1] = { ...lastMsg, text: lastTranscript.text };
                    return updated;
                } else if (transcriptions.length > prev.filter(m => m.segmentIndex !== undefined).length) {
                    return [...prev, { text: lastTranscript.text, isAgent, segmentIndex }];
                }
                return prev;
            });
        }
    }, [transcriptions]);

    useEffect(() => {
        const handleTellInfo = (e) => {
            const imageName = e.detail?.imageName;
            if (imageName) {
                setMessages(prev => [...prev, { text: `Tell me more about ${imageName}`, isAgent: false }]);
            }
        };
        window.addEventListener('tell_info_agent', handleTellInfo);
        return () => window.removeEventListener('tell_info_agent', handleTellInfo);
    }, []);

    const handleSend = async () => {
        if (!input.trim()) return;
        setMessages(prev => [...prev, { text: input, isAgent: false }]);
        try {
            const encoder = new TextEncoder();
            await room.localParticipant.publishData(encoder.encode(input), { topic: 'chat_message', reliable: true });
        } catch (err) {
            console.error("Error publishing chat_message data:", err);
        }
        setInput("");
    };

    const lastMsg = messages[messages.length - 1];
    const isThinking = (lastMsg && !lastMsg.isAgent && state !== 'speaking') || state === 'thinking';

    useEffect(() => {
        if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }, [messages, isThinking]);

    const [speakerEnabled, setSpeakerEnabled] = useState(false);

    const toggleSpeaker = async () => {
        const nextState = !speakerEnabled;
        setSpeakerEnabled(nextState);
        try {
            const encoder = new TextEncoder();
            await room.localParticipant.publishData(
                encoder.encode(JSON.stringify({ enabled: nextState })),
                { topic: 'toggle_agent_audio', reliable: true }
            );
        } catch (err) {
            console.error("Error publishing toggle_agent_audio:", err);
        }
    };

    return (
        <div className="d-flex flex-column h-100">
            <RoomAudioRenderer />
            <div className="flex-grow-1 overflow-auto d-flex flex-column gap-2 p-3" ref={boxRef}>
                    {messages?.map((msg, i) => (
                        <div key={i} className={`d-flex ${msg.isAgent ? "justify-content-start text-dark" : "justify-content-end text-light"} ${i > 0 ? "mt-2" : ""}`}>
                            <div className={`p-2 rounded fw-bold chat-msg-bubble ${msg.isAgent ? "chat-msg-agent" : "chat-msg-user"}`}>
                                {msg.text}
                            </div>
                        </div>
                    ))}
                    {isThinking && (
                        <div className="d-flex justify-content-start text-dark mt-2">
                            <div className="p-2 rounded fw-bold d-inline-flex align-items-center gap-2 bg-white chat-thinking-indicator">
                                <span className="spinner-grow spinner-grow-sm text-secondary chat-thinking-spinner" role="status"></span>
                                <span className="text-muted fst-italic">Thinking...</span>
                            </div>
                        </div>
                    )}
                    {loading === 'agent-starting' && (
                        <div className="d-flex flex-column justify-content-center align-items-center my-auto gap-2">
                            <span className="spinner-border text-secondary" style={{ width: '2rem', height: '2rem' }} role="status"></span>
                            <span className="text-muted fw-bold" style={{ fontSize: '13px', opacity: 0.8 }}>Connecting ...</span>
                        </div>
                    )}
            </div>

            <div className="d-flex align-items-center gap-2 p-2 border-top border-secondary">
                <input
                    className="form-control form-control-sm text-dark border-0 outline-0 chat-input-field"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={loading === 'agent-starting' ? "Connecting..." : "Type a message..."}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    autoFocus
                    disabled={loading === 'agent-starting'}
                />
                <button className="btn btn-sm fw-bold chat-btn-primary" onClick={handleSend} disabled={loading === 'agent-starting'}>
                    <i className="bi bi-send-fill text-white fs-6"></i>
                </button>
                <button 
                    className="btn btn-sm rounded-circle text-dark chat-btn-secondary"
                    onClick={buttonProps.onClick}
                    disabled={loading === 'agent-starting'}
                    title='Start Voice'
                >
                    {micEnabled ? (
                        <AudioWavesRenderer barCount={5} style={{ width:"22px", height:"26px", color: '#000000', '--lk-fg': '#000000', '--lk-accent-color': '#000000', display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'center' }} />
                    ) : (
                        <i className="bi bi-mic-fill"></i>
                    )}
                </button>
                <button 
                    className="btn btn-sm rounded-circle text-dark chat-btn-secondary"
                    onClick={toggleSpeaker}
                    disabled={loading === 'agent-starting'}
                    title={speakerEnabled ? 'Mute AI Voice Output' : 'Enable AI Voice Output'}
                >
                    <i className={`bi ${speakerEnabled ? "bi-volume-up-fill text-success" : "bi-volume-mute-fill"}`}></i>
                </button>
            </div>
        </div>
    );
}

function Avatar(props){
       axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;

       const domainAvatarImg = props.selectedDomain?.avatar_img || 'avatar-meera.webp';
       const avatarImgSrc = process.env.PUBLIC_URL + (domainAvatarImg.startsWith('/') ? '' : '/') + domainAvatarImg;
       const avatarName = props.selectedDomain?.avatar_name.split('-')[1] || 'Meera';

       const [room] = useState(() => new Room({}));
       const [showAvatar, setShowAvatar] = useState(false);
       const [showChatBot, setShowChatBot] = useState(false);
       const [roomName, setRoomName] = useState(null);
       const [loading, setLoading] = useState('');
       const [timeoutMessage, setTimeoutMessage] = useState(null);
       const isTextModeRef = useRef(false);

       const switchToTextMode = useCallback(async (r) => {
           if (isTextModeRef.current) return;
           isTextModeRef.current = true;
           setShowAvatar(false);
           setShowChatBot(true);
           try {
               const targetRoom = r || room;
               const encoder = new TextEncoder();
               await targetRoom.localParticipant.publishData(encoder.encode(JSON.stringify({ text_mode: true })), { topic: 'change_to_text_mode', reliable: true });
           } catch (err) {
               console.error("Error publishing change_to_text_mode:", err);
           }
       }, [room]);

       const [isMobile, setIsMobile] = useState(window.innerWidth <768);
        const [mobileTab, setMobileTab] = useState("transcript"); // "transcript" or "results"

        useEffect(() => {
           const handleResize = () => {
               setIsMobile(window.innerWidth <768);
            };
           window.addEventListener('resize', handleResize);
           return () => window.removeEventListener('resize', handleResize);
        }, []);

       useEffect(() => {
           if (isMobile && props.matches && props.matches.length > 0) {
               setMobileTab("results");
           } else if (isMobile && (!props.matches || props.matches.length === 0)) {
               setMobileTab("transcript");
           }
       }, [props.matches, isMobile]);

        useEffect(() => {
           const handleTellInfo = async (e) => {
               const imageName = e.detail?.imageName;
               if (!imageName || !room) return;
               if (isMobile) setMobileTab("transcript");
               console.log("Publishing tell_info data to agent for image:", imageName);
               try {
                   const encoder = new TextEncoder();
                   await room.localParticipant.publishData(encoder.encode(imageName), { topic: 'tell_info', reliable: true });
               } catch (err) {
                   console.error("Error publishing tell_info data:", err);
               }
           };
           window.addEventListener('tell_info_agent', handleTellInfo);
           return () => window.removeEventListener('tell_info_agent', handleTellInfo);
       }, [room, isMobile])

       // Push limit change to agent in real-time if session is active
       useEffect(() => {
           const isActive = showAvatar || showChatBot;
           if (!isActive || !room?.localParticipant) return;
           const encoder = new TextEncoder();
           room.localParticipant.publishData(
               encoder.encode(JSON.stringify({ result_limit: props.resultLimit || 10 })),
               { topic: 'update_config', reliable: true }
           ).catch(err => console.warn('update_config publish failed:', err));
       }, [props.resultLimit]);
       
        useEffect(() => {
           const handleDataReceived = (payload, participant, kind, topic) => {
               if (topic === "avatar_timeout") {
                   let msg = "Session ended due to inactivity. You can start a new session to continue.";
                   try {
                       const raw = new TextDecoder().decode(payload).trim();
                       const data = JSON.parse(raw);
                       if (data.message) msg = data.message;
                   } catch (e) {}
                   console.log("Avatar timeout received from agent:", msg);
                   setTimeoutMessage(msg);
                   setShowAvatar(false);
                   if (props.onSessionEnd) {
                       props.onSessionEnd();
                   }
               }
               if (topic === "avatar_error") {
                   console.warn("Avatar error received from backend agent, immediately switching to in-session text mode");
                   switchToTextMode(room);
               }
               if (topic === "search") {
                   const raw = new TextDecoder().decode(payload).trim();
                   try {
                       const data = JSON.parse(raw);
                       props.setMatches(data.points || []);
                       props.setQueryTags(data.query_tags || null);
                       props.setQueryDescription(data.query_description || null);
                       props.setProcessTime(data.processing_time || null);
                   } catch (e) {
                       console.error("Failed to parse search data:", e);
                   }
               }
           };
           room.on(RoomEvent.DataReceived, handleDataReceived);
           return () => room.off(RoomEvent.DataReceived, handleDataReceived);
        }, [room]);

       function getRandomInt(min, max) {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min) + min);
    }

       async function storeSession(roomName, domainId) {
        try {
            // Get IP from public API
            const ipRes = await fetch('https://api.ipify.org?format=json');
            const ipData = await ipRes.json();
            const ip = ipData.ip;
            await fetch('/api/store_session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ip: ip,
                    room_name: roomName,
                    domain_id: domainId
                })
            });
        } catch (error) {
            console.error("Error tracking user:", error);
            // Server detect from request
            try {
                await fetch('/api/store_session', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        room_name: roomName,
                        domain_id: domainId
                    })
                });
            } catch (e) {
                console.error("Error sending fallback tracking:", e);
            }
        }
    }
       const connect = async () => {
        if (props.activeSession) return;
        setTimeoutMessage(null);
        isTextModeRef.current = false;
        //create room
        const roomName = `room-${getRandomInt(1000, 9999)}`;
        setRoomName(roomName);
        const participantName = 'You';
        await Promise.all([createRoom(roomName, participantName), createAgentSession(roomName, DEFAULT_AGENT_NAME)])
        setLoading('')
        setShowAvatar(true);
        setShowChatBot(false);
        if (props.onSessionStart) {
            props.onSessionStart();
        }
    }

    const handleChatWithAI = async () => {        
        // Create new room & dispatch agent WITH text mode enabled
        setTimeoutMessage(null);
        const newRoomName = `room-${getRandomInt(1000, 9999)}`;
        setRoomName(newRoomName);
        setShowChatBot(true); // Switch to ChatBox directly
        if (props.onSessionStart) {
            props.onSessionStart();
        }
        await Promise.all([createRoom(newRoomName, 'You'), createAgentSession(newRoomName, DEFAULT_AGENT_NAME, true)]);
    }

    async function createRoom(roomName, participantName) {
        const result = await axios.get(`/getLivekitToken/${roomName}/${participantName}`)
        room.connect(process.env.REACT_APP_LIVEKIT_URL, result.data);
    }

    async function createAgentSession(roomName, agent, textMode=false) {
        setLoading('agent-starting');
        console.log('Creating session for agent:', agent, 'TextMode:', textMode);
        const metadata = {
            textMode: textMode,
            domain_id: localStorage.getItem("selected_domain_id") || "1",
            result_limit: props?.resultLimit || 10,
        };
        try {
            const result = await axios.post("/dispatch_agent", {
                agentName: agent, 
                roomName: roomName,
                metadata: metadata
            }, { headers: { "Content-Type": "application/json" } });
        }
        catch (error) {
            console.log("Error occurred at creating agent: ", error)
        }
        finally {
            console.log("Agent session created.");
        }
        const domainId = props.selectedDomain?.id || props.selectedDomain?.domain_id || localStorage.getItem("selected_domain_id");
        storeSession(roomName, domainId);  //store session history with user details e.g ip, browser
    }

    async function stopagentsession() {
        if (loading === 'agent-stopping') return;
        setLoading('agent-stopping');
        setTimeoutMessage(null);
        setShowAvatar(false);
        setShowChatBot(false)
        try {
            if (room) {
            room.disconnect();
            }
            if (roomName) {
            const result = await axios.post("/stop_agent_session", {
                "room_name": roomName
            })
            console.log(result);
            }
        }
        catch (error) {
            console.log("Error occurred during stop agent:", error)
        }
        finally {
            setLoading('')
            console.log("Agent session stopped.");
            if (props.onSessionEnd) {
                props.onSessionEnd();
            }
        }
    }

    const hasResults = props.matches && props.matches.length > 0;
    const isActiveSession = showAvatar || showChatBot;
    const isExpanded = isMobile ? (isActiveSession || hasResults) : isActiveSession;

    return (
        <RoomContext.Provider data-lk-theme="default" value={room}>
            <Container className={`agent-container position-relative border shadow-sm d-flex flex-column p-0 bg-white agent-window-base ${isExpanded ? 'agent-window-expanded' : 'agent-window-collapsed'}`}>
            
            {/* Graceful session timeout message */}
            {timeoutMessage && !showChatBot && (
                <Alert 
                    variant="warning" 
                    className="m-2 py-2 px-3 text-center shadow-sm position-absolute start-0 end-0 top-0 d-flex align-items-center justify-content-between avatar-timeout-alert" 
                    dismissible 
                    onClose={() => setTimeoutMessage(null)}
                >
                    <div className="d-flex align-items-center gap-2 text-start">
                        <i className="bi bi-clock-history text-warning fs-6"></i>
                        <span>{timeoutMessage}</span>
                    </div>
                </Alert>
            )}

            {/* Top section: Avatar Video or Photo */}
            {!showChatBot && (
                <div className="video-room position-relative bg-dark" style={{ width: '100%', aspectRatio: '3/4', overflow: 'hidden' }}>
                {showAvatar ? (
                    <AvatarRenderer 
                        avatarImgSrc={avatarImgSrc}
                        startChat={switchToTextMode}
                    />
                ) : (
                    <div className="w-100 h-100 position-relative d-flex justify-content-center align-items-center">
                        <img
                            src={avatarImgSrc}
                            alt={`${avatarName} Assistant`}
                            className="img-fluid"
                            style={{ 
                                zIndex: 2, 
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'top'
                            }}
                        />
                    </div>
                )}

                {/* Controls overlay */}
                <div className="controls position-absolute bottom-0 w-100 p-3 d-flex justify-content-center align-items-center video-controls-overlay">
                    {showAvatar ? (
                        <>
                            <MuteButton />
                            <AudioWavesRenderer/>
                            <Button
                                className="control-btn rounded-circle text-light position-relative"
                                variant="danger"
                                onClick={stopagentsession}
                                disabled={loading !== ''}>
                                <i className="bi bi-telephone-x-fill fs-6" />
                            </Button>
                        </>
                    ) : (
                        <Button 
                            className="btn-light px-4 py-2 rounded-pill shadow"
                            onClick={connect}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                if (loading === '' && !props.activeSession) {
                                    handleChatWithAI();
                                }
                            }}
                            disabled={loading !== ''}
                            style={{ zIndex: 30 }}
                        >
                            {loading === 'agent-starting' ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-mic-fill me-2"></i>
                                    Talk to {avatarName}
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </div>
        )}

            {/* Bottom section: Chat / Transcript / Results */}
            {isExpanded && (
                <div className={`transcript-box-container d-flex flex-column border-top flex-grow-1 transcript-box-base ${showChatBot ? 'transcript-box-chat-mode' : 'transcript-box-default-mode'}`}>
                    <div className="d-flex justify-content-between align-items-center p-2 border-bottom bg-light text-dark">
                        
                        {/* Header text for mobile and desktop */}
                        {(!isMobile || !hasResults) && (
                            <h6 className="m-0 fw-bold transcript-header-title">
                                {showChatBot ? `Chat with ${avatarName}` : "Transcript"}
                            </h6>
                        )}

                        {/* Mobile Header Tabs (show when has results) */}
                        {isMobile && isActiveSession && hasResults && (
                            <div className="d-flex gap-2 w-100 justify-content-center">
                                <Button 
                                    variant={mobileTab === "transcript" ? "dark" : "outline-dark"} 
                                    size="sm"
                                    className="py-1 px-3 rounded-pill fw-bold mobile-tab-btn"
                                    onClick={() => setMobileTab("transcript")}
                                >
                                    {showChatBot ? "Chat" : "Transcript"}
                                </Button>
                                <Button 
                                    variant={mobileTab === "results" ? "dark" : "outline-dark"} 
                                    size="sm"
                                    className="py-1 px-3 rounded-pill fw-bold mobile-tab-btn"
                                    onClick={() => setMobileTab("results")}
                                >
                                    Results ({props.matches.length})
                                </Button>
                            </div>
                        )}

                        {/* Mobile Stopped Session Results Title */}
                        {isMobile && !isActiveSession && hasResults && (
                            <h6 className="m-0 fw-bold transcript-header-title">
                                Results ({props.matches.length})
                            </h6>
                        )}

                        {/* Close button in top-right corner for Chat Mode */}
                        {showChatBot && (
                            <button 
                                type="button" 
                                className="btn-close ms-auto" 
                                aria-label="Close" 
                                onClick={stopagentsession}
                                disabled={loading !== ''}
                            />
                        )}
                    </div>

                    <div className={`flex-grow-1 p-2 thin-scrollbar`} style={{ overflowY: 'auto' }}>
 
                        {/* Active Session View (Desktop & Mobile unified to avoid unmounting ChatBox/TranscriptBox) */}
                        {isActiveSession && (
                            <>
                                <div className={(isMobile && mobileTab === "results") ? "d-none" : "h-100"}>
                                    {showChatBot ? <ChatBox loading={loading} setLoading={setLoading}/> : <TranscriptBox showTranscript={true} />}
                                </div>
                                {isMobile && hasResults && (
                                    <div className={mobileTab === "results" ? "h-100" : "d-none"}>
                                        <MobileResultsList matches={props.matches} activeAvatar={props.activeSession} />
                                    </div>
                                )}
                            </>
                        )}

                        {/* Mobile Stopped Session View */}
                        {isMobile && !isActiveSession && hasResults && (
                            <MobileResultsList matches={props.matches} activeAvatar={props.activeSession} />
                        )}
                    </div>
                </div>
            )}
        </Container>
        </RoomContext.Provider>
    );
}
export default Avatar;




